import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { useSelector } from "@/redux/store";
import { appSelector } from "@/redux/selectors/app.selector";
import {
  ANALYTICS_EVENTS,
  type AnalyticsEventName,
  type AnalyticsEventPropertiesMap,
} from "./events";
import type { BaseAnalyticsProperties } from "./types";

/**
 * @example
 * 
 * const { trackEvent } = useAnalytics();
 * 
 * trackEvent(ANALYTICS_EVENTS.ESTIMATE_SUBMITTED, {
 *   estimate_type: EstimateType.RECONSTRUCTION,
 *   file_count: 3,
 *   has_photos: true,
 *   has_3d_scan: false,
 * });
 */
export function useAnalytics() {
  const posthog = usePostHog();
  const location = useLocation();
  const organizationId = useSelector(appSelector.organizationId);
  const user = useSelector(appSelector.user);

  const getBaseProperties = useCallback((): BaseAnalyticsProperties => {
    return {
      page_path: location.pathname,
      organization_id: organizationId,
      user_role: user?.role,
      timestamp: new Date().toISOString(),
    };
  }, [location.pathname, organizationId, user?.role]);

  const trackEvent = useCallback(
    <E extends AnalyticsEventName>(eventName: E, properties: AnalyticsEventPropertiesMap[E]): void => {
      const baseProperties = getBaseProperties();

      posthog.capture(eventName, {
        ...baseProperties,
        ...properties,
      });

      if (import.meta.env.MODE === "development") {
        console.log(`[Analytics] ${eventName}`, { ...baseProperties, ...properties });
      }
    },
    [posthog, getBaseProperties],
  );

  return { trackEvent, ANALYTICS_EVENTS };
}
